import { useState, useEffect, useMemo, useRef } from "react";
import { Pencil, Check, X, Lock } from "lucide-react";
import { DayChip } from "@/components/shared/DayChip";
import { displayTimeTo24Hour } from "@/lib/timeFormat";
import { toast } from "sonner";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { summarizeGooglePush, googlePushOutcome, GOOGLE_SYNC_FAILED_TOAST } from "@/lib/googlePushSummary";
import { useKinCalendarFeed } from "@/hooks/useKinCalendarFeed";
import { isAllDayMarker } from "@/lib/eventTimeSemantics";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { useIsPro } from "@/hooks/useIsPro";
import { ProUpsellLink, useProUpsellToast } from "@/components/ProUpsell";
import { supabase } from "@/integrations/supabase/client";
import { trackFeature } from "@/lib/analytics";
import { logActivity } from "@/lib/activityLog";
import {
  resolveAppleHandoff,
  skippedMatchRowIds,
  type AppleHandoff,
} from "@/lib/appleIcsHandoff";
import {
  checkEventDuplicatesBatch,
  partitionDedupResults,
  type CandidateEvent,
  type DedupResult,
} from "@/lib/eventDedup";
import { reportError } from "@/lib/reportError";
import { writeKinEvents, type KinEventDraft } from "@/lib/kinEventWrite";
import { REMINDER_OPTIONS, reminderMinutesOf, type ReminderValue } from "@/lib/reminderOptions";
import { matchKidInText, kidMatchNotice, type KidMemoryFact } from "@/lib/kidChipMatch";
import { usePreferences } from "@/hooks/useUserPreferences";
import { FAMILY_CHIP, WORK_CHIP } from "@/components/AddEventSheet";
import { openExternal } from "@/lib/openExternal";
import { isMedianShellUA } from "@/lib/platform";

export interface ScheduleEvent {
  title: string;
  date: string;
  time: string;
  location: string;
  notes: string;
}

interface SchedulePreviewProps {
  events: ScheduleEvent[];
  followUpText?: string;
  onSendMessage?: (msg: string) => void;
  /** When true, shows the primary "Save to Kin" button above Google/Apple. */
  enableKinSave?: boolean;
  /** Called after a successful Kin save so the host modal can close itself. */
  onKinSaveComplete?: () => void;
  /** Source tag stamped onto saved imported_events rows. Defaults to "schedule".
   *  Pass "newsletter" from the Newsletter Scanner flow and "work_calendar" from
   *  the Work Calendar import flow so saved events are correctly categorized. */
  initialSourceType?: "schedule" | "newsletter" | "work_calendar";
  /** Renders a checkbox per event (opt-out selection, defaults all checked)
   *  instead of the normal "every visible row is included" model. Used by
   *  the newsletter-email review screen — a PTA newsletter can have 9 dated
   *  items and she may only want 2. */
  selectable?: boolean;
  /** The staged imported_events batch (status='pending_review') this preview
   *  was loaded from. Once she saves any subset, the whole staged batch is
   *  deleted — selected events get re-inserted fresh as status='active'
   *  through the normal dedup + save pipeline below, and whatever she didn't
   *  select is discarded for good. Pass alongside `selectable`. */
  pendingBatchId?: string;
}

const FREE_EVENT_LIMIT = 10;
const FREE_MONTHLY_IMPORTS = 3;

// #1957 — ReminderValue and REMINDER_OPTIONS moved to src/lib/reminderOptions.ts
// so the Add-event sheet offers the same choices. Imported above; the parity
// test there pins the three copies that cannot import it (two Deno, one an
// allowlist that silently falls back to "none").

// The .ics is no longer built here. reminderToIcsDuration / toIcsDateTime /
// addHourIcs / escapeIcsText / buildIcsFile all moved server-side into
// supabase/functions/schedule-ics/index.ts, which serves the same content at a
// real https URL with Content-Type: text/calendar. They are deleted rather
// than kept "just in case" on purpose: what they produced was a
// data:text/calendar URI, and Median's WKWebView has now intercepted that as a
// generic file download twice (Apr 29 and again after the Jul 23-25 shell
// rebuild). Leaving a second, unused builder in the tree is how the data: URI
// path finds its way back in. toISO / addHour below stay — they feed the
// Google Calendar push, which is a JSON payload, not an .ics.

function toISO(date: string, time: string): string {
  const [year, month, day] = date.split("-").map(Number);
  let [timePart, meridiem] = time.split(/\s+/);
  let [hours, minutes] = timePart.split(":").map(Number);
  if (meridiem?.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (meridiem?.toUpperCase() === "AM" && hours === 12) hours = 0;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(minutes || 0).padStart(2, "0")}:00`;
}

function addHour(iso: string): string {
  const d = new Date(iso);
  d.setHours(d.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

/** Projects a dedup result's candidate back to the plain ScheduleEvent shape
 *  the .ics builder and Google push expect. Used by confirmReviewAndSave to
 *  turn its already-resolved pendingCleanResults/acceptedReview/
 *  pendingSkippedResults arrays into exportable event lists. */
function dedupResultToScheduleEvent({ candidate }: DedupResult): ScheduleEvent {
  return {
    title: candidate.title,
    date: candidate.event_date,
    time: candidate.start_time ?? "",
    location: candidate.location ?? "",
    notes: candidate.notes ?? "",
  };
}

// DayChip lifted to src/components/shared/DayChip.tsx (Pass 1, May 15) so the
// Today tab reschedule button can render the same picker. SchedulePreview now
// imports it; behavior and styling are unchanged.
const SchedulePreview = ({ events: initialEvents, followUpText, onSendMessage, enableKinSave, onKinSaveComplete, initialSourceType, selectable, pendingBatchId }: SchedulePreviewProps) => {
  const { user, session, adminLoaded, proLoaded } = useAuth();
  const isAdmin = useAdmin();
  const isPro = useIsPro();
  const proUpsellToast = useProUpsellToast();
  // Issue #363: the free-tier caps below used to bypass on `isAdmin` alone, so
  // a paying non-admin Pro subscriber was capped at 10 events per import and
  // hard-blocked after 3 imports/month. Pro bypasses too now.
  //
  // `useAdmin()` and `useIsPro()` each fold in their own loaded flag, so BOTH
  // read false while their row is in flight — meaning `isAdmin || isPro` is
  // also false during that window. Slicing on it would silently drop events
  // 11+ from every save path (Save to Kin, Google, .ics) on a cold open, so
  // the cap simply does not exist until both reads have landed. The render
  // gate further down (`!checkedLimit || !tierResolved`) keeps the preview —
  // and therefore every save button — off screen until then.
  const tierResolved = adminLoaded && proLoaded;
  const limitApplies = tierResolved && !isAdmin && !isPro;
  const { connected, connect, addEvents, disconnect: disconnectGoogle } = useGoogleCalendar();
  // #1647 — when her Apple Calendar already subscribes to Kin, a plain Save to
  // Kin IS the Apple save: the per-import handoff button would be a second,
  // pointless tap, so it is hidden and the toast says where the events went.
  const { active: kinFeedActive } = useKinCalendarFeed();
  const [events, setEvents] = useState(initialEvents);
  const [originalTitles] = useState(() => initialEvents.map((e) => e.title));
  // Which rows are included when `selectable` is on — defaults to all,
  // she unchecks the ones she doesn't want. Ignored otherwise.
  const [selected, setSelected] = useState<Set<number>>(() => new Set(initialEvents.map((_, i) => i)));
  // Tracks which indices the user has manually edited so bulk apply skips them.
  const [manuallyEdited, setManuallyEdited] = useState<Set<number>>(new Set());
  const [bulkLabel, setBulkLabel] = useState("");
  const [appliedLabel, setAppliedLabel] = useState("");
  const [reminder, setReminder] = useState<ReminderValue>("none");

  // ── #1957 item 6 — "Who's this for?" ────────────────────────────────────
  //
  // The chip sets `child` on every row of the import, which is the field the
  // calendar colors by. It was hardcoded `child: null`, which is why every
  // imported event rendered in family periwinkle however obviously it belonged
  // to one kid.
  const { prefs } = usePreferences();
  const kids = useMemo(
    () => (prefs?.known_kid_names ?? []).filter((n) => typeof n === "string" && n.trim()),
    [prefs?.known_kid_names],
  );
  const [who, setWho] = useState<string>(FAMILY_CHIP);
  // Set only while the selection is still Kin's guess. Her tap clears it, so
  // "Looks like this is for C" never sits over a chip she chose herself.
  const [whoGuess, setWhoGuess] = useState<string | null>(null);
  const [kidFacts, setKidFacts] = useState<KidMemoryFact[]>([]);
  /** True once she has touched a chip — stops any later auto-guess. */
  const whoTouchedRef = useRef(false);
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  // Index of the event row whose day picker is currently expanded. Only one
  // picker can be open at a time across all rows.
  const [openPickerIdx, setOpenPickerIdx] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<ScheduleEvent | null>(null);
  /** Apple Calendar handoff for the in-flight / just-finished Save to Kin.
   *  null until the combined button is tapped. Only ever set to a "ready"
   *  URL by resolveAppleHandoff, which cannot produce one from a save that
   *  threw — that's what keeps a failed save from sending her to a calendar
   *  file full of events that aren't actually in Kin.
   *
   *  Replaces the old `icsState`, which had been dead since the standalone
   *  Apple button was retired: nothing set it to anything but "idle", so the
   *  instruction card it gated never rendered. */
  const [appleHandoff, setAppleHandoff] = useState<AppleHandoff | null>(null);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [checkedLimit, setCheckedLimit] = useState(false);
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const suggestionsRequested = useRef(false);
  const [kinSaveState, setKinSaveState] = useState<"idle" | "loading" | "review" | "success">("idle");
  const [reviewResults, setReviewResults] = useState<DedupResult[]>([]);
  const [reviewChecked, setReviewChecked] = useState<Set<number>>(new Set());
  const [pendingCleanResults, setPendingCleanResults] = useState<DedupResult[]>([]);
  // Genuine duplicates the dedup RPC auto-skipped (action: 'skip'). Carried
  // through the review detour so the final toast/export can account for them
  // instead of silently discarding — a 0-inserted save can still mean
  // "already saved", not "nothing happened."
  const [pendingSkippedResults, setPendingSkippedResults] = useState<DedupResult[]>([]);
  const [savedCount, setSavedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  // Source-type tag stamped onto every saved imported_events row. Seeded from
  // the host flow (schedule / newsletter / work_calendar) so the same Save to
  // Kin pipeline below works for all three without copy-paste.
  const [sourceType, _setSourceType] = useState<"schedule" | "newsletter" | "work_calendar">(
    initialSourceType ?? "schedule"
  );
  void _setSourceType;

  useEffect(() => {
    if (!user) return;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
    supabase
      .from("schedule_imports")
      .select("id", { count: "exact" })
      .eq("user_id", user.id)
      .gte("created_at", monthStart)
      .lt("created_at", monthEnd)
      .then(({ count }) => {
        setMonthlyCount(count ?? 0);
        setCheckedLimit(true);
      });
  }, [user]);

  const activeEvents = events.filter((_, i) => !removed.has(i));
  // When selectable, only checked rows actually get saved/exported — activeEvents
  // itself stays the "not manually removed" set so the render loop's
  // activeEvents.indexOf(ev) lookups (used for the free-tier lock cutoff) are
  // unaffected by which boxes happen to be checked.
  const includedEvents = selectable ? events.filter((_, i) => !removed.has(i) && selected.has(i)) : activeEvents;
  const limitedEvents = limitApplies ? includedEvents.slice(0, FREE_EVENT_LIMIT) : includedEvents;
  const lockedEvents = limitApplies ? includedEvents.slice(FREE_EVENT_LIMIT) : [];
  const importsExhausted = limitApplies && monthlyCount >= FREE_MONTHLY_IMPORTS;

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditValues({ ...events[idx] });
  };

  const confirmEdit = () => {
    if (editingIdx !== null && editValues) {
      const idx = editingIdx;
      setEvents(prev => prev.map((e, i) => i === idx ? editValues : e));
      // Mark this event as manually edited so future bulk applies skip it.
      setManuallyEdited(prev => {
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
    }
    setEditingIdx(null);
    setEditValues(null);
  };

  // Family memory: the school / team / teacher facts that name a kid. Read
  // best-effort — a failure here costs the memory pass and nothing else, so it
  // never blocks the preview or the save.
  useEffect(() => {
    if (!user || kids.length === 0) { setKidFacts([]); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase as any)
        .from("family_context")
        .select("subject, value")
        .eq("user_id", user.id)
        .eq("archived", false)
        .not("subject", "is", null)
        .limit(200);
      if (cancelled) return;
      if (error) {
        console.warn("[SchedulePreview] kid memory read failed", error);
        setKidFacts([]);
        return;
      }
      setKidFacts(Array.isArray(data) ? (data as KidMemoryFact[]) : []);
    })();
    return () => { cancelled = true; };
  }, [user?.id, kids.length]);

  /** Every bit of text this import gives us to identify a kid from: the label
   *  she is typing plus every event's title, location and notes — which is
   *  where the OCR'd school / team / teacher name actually lands. */
  const importText = useMemo(
    () =>
      [bulkLabel, ...events.flatMap((e) => [e.title, e.location, e.notes])]
        .filter(Boolean)
        .join(" \n "),
    [bulkLabel, events],
  );

  // The guess. Re-runs as she types in the label box, which is what makes
  // "C's School" select C's chip — and stops for good the moment she taps a
  // chip herself, so Kin never overrides her.
  useEffect(() => {
    if (whoTouchedRef.current || kids.length === 0) return;
    const match = matchKidInText(importText, kids, kidFacts);
    if (match) {
      setWho(match.kid);
      setWhoGuess(kidMatchNotice(match));
    } else {
      // ⚠️ Falls BACK to Family when a guess stops matching. Without this,
      // typing "C's school" and then clearing the box would leave C's chip
      // selected with no reason on screen, and the import would save under a
      // kid nothing on the card mentions any more.
      setWho(FAMILY_CHIP);
      setWhoGuess(null);
    }
  }, [importText, kids, kidFacts]);

  const chips = useMemo(
    () => [
      ...kids.map((name) => ({ key: name, label: name })),
      { key: FAMILY_CHIP, label: "Family" },
      { key: WORK_CHIP, label: "Work" },
    ],
    [kids],
  );

  const selectWho = (key: string) => {
    whoTouchedRef.current = true;
    setWho(key);
    setWhoGuess(null);
  };

  /** What the chip means for the row we write. `child` is the colored field;
   *  Work rides on source_type, which is the rule the dots already read. */
  const chosenChild = who === FAMILY_CHIP || who === WORK_CHIP ? null : who;

  const applyBulkLabel = () => {
    const label = bulkLabel.trim();
    if (!label) return;
    setEvents(prev => prev.map((e, i) => {
      if (manuallyEdited.has(i)) return e;
      const base = originalTitles[i] ?? e.title;
      return { ...e, title: `${label} - ${base}` };
    }));
    setAppliedLabel(label);
  };

  const clearBulkLabel = () => {
    setEvents(prev => prev.map((e, i) => {
      if (manuallyEdited.has(i)) return e;
      return { ...e, title: originalTitles[i] ?? e.title };
    }));
    setBulkLabel("");
    setAppliedLabel("");
  };

  const reminderMinutes = reminderMinutesOf(reminder);

  // #1806 — the standalone "Add to Google Calendar" handler (`handleAdd`) and
  // its `addState` were removed here. The only thing that ever rendered it was
  // its own error-state retry button, and `addState` only left "idle" inside
  // the handler itself — so it had been unreachable since the combined
  // "Save to Kin + Google Calendar" button replaced it. It also logged its
  // own `schedule_imports` row client-side (at the time `add_events` wrote one
  // server-side too — that insert was removed in the same change, so the one
  // row per import is now insertKinEvents'), and it read nothing back from
  // Google before reporting success. The combined path (`finishSave`, Google
  // branch) is the one Google writer now, and it reads the per-event ledger.


  /*
   * ⚠️ #1957 item A's `warnReminderNeedsExport` is DELETED here (#1966), and
   * its deletion is the point rather than a tidy-up.
   *
   * That toast said "Saved! Reminders come from Google or Apple Calendar, so
   * tap 🍎 or Google to get one." It was true only because the value was being
   * thrown away: `reminderMinutes` reached the Google push payload and the
   * Apple `.ics` URL and nothing else, so a plain Save to Kin really did
   * discard her choice.
   *
   * `reminder_minutes` is a column on both tables now. The value survives the
   * save, the SUBSCRIBED Apple calendar emits it as a VALARM on its next pull
   * with nothing tapped, and a Google push days later reads it off the row. So
   * the warning would now fire at exactly the moment the reminder is no longer
   * lost — the worst time to tell someone something is broken.
   */

  /** Apple Calendar handoff.
   *
   *  There is deliberately no downloadIcsFile here any more, and no
   *  navigation of any kind inside a save handler. The .ics is served by the
   *  schedule-ics Edge Function at a real https URL, and the user reaches it
   *  by tapping the link that renders AFTER the save has been awaited to
   *  completion (see the handoff card in the render below).
   *
   *  That ordering is the fix for two compounding bugs, and reversing either
   *  half brings both back:
   *
   *    - Firing the handoff first (f7dc0c4, Jul 13) was how a force-quit
   *      during Median's file sheet could lose the whole batch. It only ever
   *      raced the write rather than reliably beating it, which is why it
   *      read as harmless for three weeks.
   *    - The reason it had to fire first was window.open()'s user-gesture
   *      deadline. A plain <a href> tapped by the user has no such deadline,
   *      so once the .ics is a real URL the deadline stops existing and the
   *      save can be awaited properly.
   *
   *  The old standalone handleAppleCalendar went with it: nothing rendered a
   *  button for it, and it was the last caller of the data: URI path. */

  /** Insert a list of dedup results into imported_events under a shared batch_id.
   *  Returns how many events were inserted and whether the one
   *  `schedule_imports` row for this import — the thing the free tier's
   *  monthly cap counts — was confirmed written (#1806). */
  const insertKinEvents = async (
    resultsToInsert: DedupResult[],
    batchId: string
  ): Promise<{ inserted: number; importLogged: boolean; insertedIds: string[] }> => {
    if (!user || resultsToInsert.length === 0) return { inserted: 0, importLogged: false, insertedIds: [] };
    const rows: KinEventDraft[] = resultsToInsert.map(({ candidate }) => {
      // Normalize the display time ("3:00 PM") to the canonical 24-hour
      // "HH:MM:00" the events table expects, using the SAME shared helper every
      // other events-table writer uses (Issue #79 / PR #69). The old local
      // `timeToPostgresTime` parser was a divergent second implementation — the
      // exact "same bug on multiple write paths" shape that let display-format
      // rows leak in. `displayTimeTo24Hour` returns null for TBD/all-day/
      // unparseable, so a null result also drives is_all_day.
      const start24 = displayTimeTo24Hour(candidate.start_time);
      // #1806's client-generated id, the batch_id and `status` now come from
      // writeKinEvents, which owns the row shape for both tables.
      return {
        title: candidate.title,
        event_date: candidate.event_date,
        start_time: start24,
        end_time: null,
        // #607 — an unparseable/absent time is TBD, NOT all-day. Falling back
        // to `start24 === null` made every unknown time claim to be an all-day
        // event, which suppresses the "What time?" Quick Question that exists
        // to recover it. Only an explicit all-day marker means all-day.
        is_all_day: candidate.is_all_day ?? false,
        location: candidate.location ?? null,
        notes: candidate.notes ?? null,
        // #1957 item 6 — was hardcoded `null`, which is why every imported
        // event rendered in family periwinkle however clearly it belonged to
        // one kid. `child` is the field the dots, the bars and the legend all
        // color by; there is no separate color column.
        child: chosenChild,
      };
    });
    // #1957 — the imported_events insert and its `events` mirror now live in
    // writeKinEvents, so the Add-event sheet saves through this exact path
    // instead of growing a second writer that could forget the mirror (which
    // is the only thing kin-calendar-feed serves, i.e. the only thing that
    // reaches her Apple Calendar). Row shape, ids, category and the Sentry
    // report are unchanged — they moved, they did not change.
    const { insertedIds, error } = await writeKinEvents({
      userId: user.id,
      drafts: rows,
      // The Work chip is a source_type, not a child: that is the rule
      // eventPersonColor already reads for the legend's slate, and inventing a
      // child called "Work" would put a fake kid in her legend. Only overrides
      // the card's own sourceType when she actually picked Work — a newsletter
      // import must stay "newsletter".
      sourceType: who === WORK_CHIP ? "work_calendar" : sourceType,
      batchId,
      // #1966 — stored, not just handed to whichever export she happens to
      // tap. Same value the Google payload and the .ics URL already used, so
      // the three cannot disagree about what she picked.
      reminderMinutes,
    });
    if (error) throw error;
    // #1806 — this is now the ONLY schedule_imports writer (the server-side
    // one in add_events is gone), so its result is read, not assumed:
    // supabase-js resolves on a refused insert rather than throwing, and the
    // on-screen monthly count must follow what the table actually holds.
    const { error: importErr } = await supabase.from("schedule_imports").insert({
      user_id: user.id,
      event_count: rows.length,
    });
    if (importErr) {
      reportError("[SchedulePreview] schedule_imports row failed (insertKinEvents)", importErr, {
        tags: { area: "schedule-preview", writer: "insertKinEvents" },
        extra: { source_type: sourceType, batch_id: batchId, count: rows.length },
      });
    }
    trackFeature("schedule_import", { destination: "kin", event_count: rows.length, source_type: sourceType });
    logActivity("schedule_import", { destination: "kin", event_count: rows.length, source_type: sourceType });
    return { inserted: rows.length, importLogged: !importErr, insertedIds };
  };

  /** Deletes the staged pending_review batch (see `pendingBatchId` prop doc).
   *  Called once per real Save attempt, regardless of how many candidates
   *  actually got inserted vs. auto-skipped as duplicates — either way she's
   *  acted on the batch, so the staged rows are consumed. Deliberately NOT
   *  folded into insertKinEvents: that function early-returns 0 without
   *  touching the DB when every candidate was a dedup skip, which would
   *  otherwise leave the pending batch stuck forever after a real save tap. */
  const cleanupPendingBatch = async () => {
    if (!pendingBatchId || !user) return;
    const { error } = await supabase
      .from("imported_events")
      .delete()
      .eq("user_id", user.id)
      .eq("batch_id", pendingBatchId)
      .eq("status", "pending_review");
    if (error) reportError("[SchedulePreview] pending batch cleanup failed", error, { tags: { area: "schedule-preview" } });
  };

  // When true, the in-flight Save to Kin will also download an .ics file and
  // stamp `apple_exported_at` on the saved rows. Set by the combined button so
  // both the no-review and the review-confirm paths honor the export.
  const wantAppleExportRef = useRef(false);
  // When true, the in-flight Save to Kin will also push events to Google
  // Calendar via the existing edge function and write back google_event_id.
  const wantGoogleExportRef = useRef(false);

  // (Google Calendar payload shape is built inline below using the existing
  //  useGoogleCalendar.addEvents AddEventInput contract.)

  /** After a successful insert, optionally download .ics + stamp
   *  apple_exported_at, and/or push to Google Calendar + write back
   *  google_event_id. Returns the toast message to show.
   *
   *  `skippedResults` are the candidates the dedup RPC auto-skipped as
   *  genuine duplicates (action: 'skip') — already saved from an earlier
   *  attempt. They're not re-inserted, but they're real events the user
   *  selected, so the Apple export still needs to hand them off and the
   *  Google branch below still has to decide whether they made it onto the
   *  user's actual Google Calendar (see that branch). Only the toast copy
   *  and the newly-`inserted` count treat them as zero.
   *
   *  `googleEventsForExport` feeds the Google push branch only. finishSave
   *  does no Apple navigation of any kind — it stamps apple_exported_at and
   *  appends the toast suffix, and the caller resolves the handoff link once
   *  this resolves. It carries ONLY the freshly-inserted events (in insertion
   *  order); the skipped ones are handled from `skippedResults` inside the
   *  Google branch.
   *
   *  `insertedRowIds` are those same events' imported_events ids, in the same
   *  order — the ids insertKinEvents generated and inserted (#1806). The
   *  Google write-back matches Google's answers to rows by position, so the
   *  mapping has to be the one the insert actually used, not a re-fetch
   *  ordered by a timestamp every row of the batch shares. */
  const finishSave = async (
    batchId: string,
    inserted: number,
    skippedResults: DedupResult[],
    googleEventsForExport: ScheduleEvent[],
    insertedRowIds: string[]
  ): Promise<string> => {
    const skipped = skippedResults.length;
    let msg =
      inserted === 0 && skipped > 0
        ? "Already in your week 💜"
        : skipped > 0
          ? `Saved to Kin 💜 ${inserted} ${inserted === 1 ? "event" : "events"} added (${skipped} already saved)`
          : `Saved to Kin 💜 ${inserted} ${inserted === 1 ? "event" : "events"} added to your week`;
    if (inserted === 0 && skipped === 0) return msg;

    // Apple .ics stamp branch. No navigation here — this records
    // apple_exported_at on the newly-inserted rows and appends the toast
    // suffix; the handoff link itself is resolved by the caller after this
    // returns. Runs whenever the user chose the Apple option and there's
    // anything to acknowledge — including when everything was a dedup skip
    // (inserted === 0, skipped > 0).
    if (wantAppleExportRef.current) {
      try {
        if (user && inserted > 0) {
          const appleTs = new Date().toISOString();
          await (supabase.from as any)("imported_events")
            .update({ apple_exported_at: appleTs })
            .eq("user_id", user.id)
            .eq("batch_id", batchId);
          // events dual-write
          const { error: appleEvtErr } = await (supabase.from as any)("events")
            .update({ apple_exported_at: appleTs })
            .eq("user_id", user.id)
            .eq("batch_id", batchId);
          if (appleEvtErr) console.error("[SaveToKin+Apple] events export marker dual-write failed", appleEvtErr);
        }
        // #1639 — this used to say "+ calendar file downloaded". Nothing has
        // downloaded since the handoff became a link she taps (Aug 4); the
        // claim was false, and it told her to look for a file that never came.
        // #1645 — and then it said "below", while the toast renders
        // bottom-center and the card sits ABOVE it. Name the button, never a
        // direction: position words go stale the moment either element moves.
        msg = `${msg} · now tap 🍎 Add to Apple Calendar to finish`;
      } catch (err) {
        reportError("[SaveToKin+Apple] export step failed (non-blocking)", err, { tags: { area: "schedule-preview" } });
      }
    } else if (kinFeedActive && inserted > 0) {
      // #1647 — Apple subscribes to Kin, so these land there on Apple's next
      // pull. Honest tense: "it'll show", not "it's on".
      msg = `${msg} · it'll show on your Apple Calendar too 🍎`;
    }

    // Google Calendar push branch — use the existing useGoogleCalendar.addEvents
    // (which talks to the cross-project edge function with token refresh).
    // Runs whenever the user chose Google and there's anything to acknowledge —
    // including when everything was a dedup skip (inserted === 0, skipped > 0),
    // mirroring the Apple branch fix. "Already saved to Kin" says nothing about
    // whether an event ever reached the user's actual Google Calendar, so we
    // check each skipped event's existing row for a google_event_id and push
    // only the ones genuinely missing one — never re-push, never overwrite.
    if (wantGoogleExportRef.current && user && inserted + skipped > 0) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
        /** #1966 — `rowReminder` is the reminder STORED ON THE ROW, and it is
         *  only ever passed for a dedup-SKIPPED row.
         *
         *  Her item 10 says the Google export reads `reminder_minutes` from the
         *  row rather than from React state. For a freshly inserted row those
         *  are the same value by construction (writeKinEvents just stamped the
         *  batch's choice onto it), so `undefined` falls back to the batch and
         *  that is not a shortcut.
         *
         *  A skipped row is the case where they diverge: it was saved on an
         *  earlier pass, possibly with a different reminder, and is reaching
         *  Google for the first time now. Using the current picker there would
         *  silently drop the alert it was saved with — exactly what storing the
         *  column was meant to stop.
         *
         *  ⚠️ `undefined` and `null` are different answers. `undefined` means
         *  "no row value, use the batch"; `null` means the row says NO reminder
         *  and the batch must not reinstate one. So the check is `=== undefined`
         *  and never a falsy test — 0 ("at the time of the event") is real. */
        const buildPayload = (ev: ScheduleEvent, rowReminder?: number | null) => {
          const start = toISO(ev.date, ev.time && ev.time.toUpperCase() !== "TBD" ? ev.time : "9:00 AM");
          const mins = rowReminder === undefined ? reminderMinutes : rowReminder;
          return {
            title: ev.title,
            start_datetime: start,
            end_datetime: addHour(start),
            location: ev.location || "",
            timezone: tz,
            notes: ev.notes || "",
            ...(mins !== null ? { reminder_minutes: mins } : {}),
          };
        };

        // (a) Freshly-inserted rows for this batch, in insertion order — each
        //     one just got created with no google_event_id, so all get pushed.
        //     The ids come from the insert itself (client-generated, #1806),
        //     NOT from a re-fetch ordered by created_at: every row of a
        //     multi-row insert shares one timestamp, so that order was
        //     undefined and a Google id could be written onto the wrong event.
        let insertedRows: Array<{ id: string }> = [];
        if (inserted > 0) {
          if (insertedRowIds.length !== googleEventsForExport.length) {
            throw new Error(
              `Expected ${googleEventsForExport.length} inserted row ids but got ${insertedRowIds.length}`
            );
          }
          insertedRows = insertedRowIds.map((id) => ({ id }));
        }

        // (b) Dedup-skipped rows already exist in Kin from a prior save. The
        //     dedup match carries the existing imported_events.id (an exact
        //     match is guaranteed for action:'skip'). Fetch those rows' current
        //     google_event_id: push ONLY the ones that are still null (never
        //     actually connected to Google — e.g. saved to Kin/Apple only on
        //     their first pass). This is what prevents both silent skips and
        //     duplicate Google events. Dedupe by rowId so two identical
        //     candidates can't push the same existing event twice.
        const skippedRowIds = Array.from(
          new Set(
            skippedResults
              .map((r) => r.matches.find((m) => m.match_type === "exact")?.id ?? r.matches[0]?.id)
              .filter((id): id is string => Boolean(id))
          )
        );
        const skippedTargets: Array<{
          event: ScheduleEvent;
          rowId: string;
          reminderMinutes: number | null;
        }> = [];
        if (skippedRowIds.length > 0) {
          // `reminder_minutes` is named here deliberately: this is an explicit
          // select list, so a column left out of it comes back `undefined`
          // rather than erroring, and the payload would quietly fall back to
          // the batch value for every skipped row.
          const { data: existing, error: existingErr } = await (supabase.from as any)("imported_events")
            .select("id, google_event_id, reminder_minutes")
            .eq("user_id", user.id)
            .in("id", skippedRowIds);

          if (existingErr) {
            console.warn(
              "[SaveToKin+Google] failed to load existing rows; skipping skipped-row push to avoid duplicates",
              existingErr
            );
          } else {
            const gidByRow = new Map<string, string | null>(
              (Array.isArray(existing) ? existing : []).map((r: any) => [r.id, r.google_event_id])
            );
            const reminderByRow = new Map<string, number | null>(
              (Array.isArray(existing) ? existing : []).map((r: any) => [
                r.id,
                typeof r.reminder_minutes === "number" ? r.reminder_minutes : null,
              ])
            );
            const seen = new Set<string>();
            for (const r of skippedResults) {
              const rowId = r.matches.find((m) => m.match_type === "exact")?.id ?? r.matches[0]?.id;
              if (!rowId || seen.has(rowId)) continue;
              seen.add(rowId);
              const gid = gidByRow.get(rowId);
              if (gid === null) {
                skippedTargets.push({
                  event: dedupResultToScheduleEvent(r),
                  rowId,
                  reminderMinutes: reminderByRow.get(rowId) ?? null,
                });
              }
            }
          }
        }

        // Unified push list: inserted rows (matched by insertion order) +
        // skipped rows genuinely missing a Google connection. rowId is the
        // imported_events row that each pushed event's google_event_id gets
        // written back to.
        const pushList: Array<{
          event: ScheduleEvent;
          rowId: string | null;
          /** undefined for a freshly inserted row — see buildPayload. */
          reminderMinutes?: number | null;
        }> = [
          ...googleEventsForExport.map((ev, i) => ({ event: ev, rowId: insertedRows[i]?.id ?? null })),
          ...skippedTargets.map((t) => ({
            event: t.event,
            rowId: t.rowId,
            reminderMinutes: t.reminderMinutes,
          })),
        ];

        if (pushList.length === 0) {
          // Everything the user selected is already on Google — nothing to
          // push, but confirm that honestly instead of silently doing nothing.
          msg = `${msg} (already on Google)`;
        } else {
          const resp = (await addEvents(
            pushList.map((p) => buildPayload(p.event, p.reminderMinutes))
          )) as
            | { events?: unknown }
            | undefined;

          // #1806 — READ the ledger before saying anything. `add_events`
          // answers 200 for the BATCH whatever Google said about each event;
          // with a dead token every row is `success: false` and the status is
          // still 200, so the catch below never fires. `summary.ids` is null
          // wherever Google refused, so a refused row gets no id written back
          // and the next import's dedup (#606) still sees it as unsynced.
          const summary = summarizeGooglePush(resp?.events, pushList.length);
          await Promise.all(
            pushList.map(async (p, i) => {
              const gid = summary.ids[i];
              if (!gid || !p.rowId) return;
              // BOTH writes awaited. A PostgREST builder only sends its request
              // when it is awaited/then'ed, so the `void` that used to sit on
              // the events mirror meant that update never ran at all — the
              // Today card's row never learned its Google id even on a good
              // push. The results are read, not assumed (house rule 16).
              const [mirror, imported] = await Promise.all([
                (supabase.from as any)("events").update({ google_event_id: gid }).eq("source_id", p.rowId),
                (supabase.from as any)("imported_events").update({ google_event_id: gid }).eq("id", p.rowId),
              ]);
              if (mirror?.error) {
                reportError("[SaveToKin+Google] google_event_id write-back to events failed", mirror.error, {
                  tags: { area: "schedule-preview" },
                  extra: { row_id: p.rowId },
                });
              }
              if (imported?.error) {
                reportError("[SaveToKin+Google] google_event_id write-back to imported_events failed", imported.error, {
                  tags: { area: "schedule-preview" },
                  extra: { row_id: p.rowId },
                });
              }
            })
          );
          const outcome = googlePushOutcome(summary, pushList.length);
          // An auth refusal means the token is dead, not one event malformed.
          // Disconnect for REAL — the server deletes the token row — because
          // check_status answers "connected" whenever that row exists and the
          // #1798 resume effect re-runs it on every app resume, so an in-memory
          // flag alone would read Not connected only until she next came back.
          let ownToast = outcome.toast;
          if (outcome.markDisconnected) {
            const gone = await disconnectGoogle();
            if (!gone) {
              // Google refused the token AND we could not clear it. Saying
              // "disconnected" now would be a claim the next resume disproves.
              // With nothing landed, say the true thing — saved to Kin, Google
              // did not take it. With a partial landing the "N of M" suffix
              // below is already the true thing, so no extra toast.
              reportError(
                "[SaveToKin+Google] Google refused the token but disconnect did not land",
                new Error("disconnect_failed"),
                { tags: { area: "schedule-preview" } }
              );
              ownToast = outcome.suffix === null ? GOOGLE_SYNC_FAILED_TOAST : null;
            }
          }
          // On its own when nothing reached Google (the success toast is then
          // suppressed — same exit the catch below takes), or alongside the
          // "N of M" suffix when the token died partway through the batch.
          if (ownToast) toast(ownToast);
          if (outcome.suffix === null) return "";
          msg = `${msg}${outcome.suffix}`;
        }
      } catch (err) {
        reportError("[SaveToKin+Google] push failed (non-blocking)", err, { tags: { area: "schedule-preview" } });
        const message = err instanceof Error ? err.message : "";
        if (message.includes("TOKEN_EXPIRED") || message.includes("401") || message.includes("403")) {
          toast("Google Calendar disconnected. Reconnect in settings.");
        } else {
          toast("Saved to Kin 💜 but Google Calendar sync failed. Try again later.");
        }
        // Suppress the default success toast so we don't double-toast.
        return "";
      }
    }
    return msg;
  };

  const handleSaveToKin = async () => {
    if (!user) {
      toast.error("Please sign in to save to Kin.");
      return;
    }
    if (kinSaveState === "loading") return;
    // Belt-and-suspenders on the render gate: never write while the tier reads
    // are unresolved. limitedEvents is the list every save path uses, and an
    // unresolved read is exactly when we can't say whether it's complete.
    if (!tierResolved) return;
    setKinSaveState("loading");

    try {
      // Free tier monthly cap (admins and Pro subscribers bypass — #363)
      if (limitApplies) {
        const { data: countData, error: countErr } = await (supabase.rpc as any)(
          "get_monthly_import_count",
          { p_user_id: user.id }
        );
        if (!countErr && typeof countData === "number" && countData >= FREE_MONTHLY_IMPORTS) {
          // Same family as kin-inbox's free-tier cap push copy (a separate
          // budget, but she shouldn't read them as two different systems) —
          // see supabase/functions/kin-inbox/index.ts's freeTierCapPush().
          // Issue #50: same copy, but the toast now carries a "See Pro" action
          // instead of dead-ending on the word "Upgrade".
          proUpsellToast(
            "You've used your 3 free imports this month 💜",
            "Kin Pro gives you unlimited schedule imports."
          );
          setKinSaveState("idle");
          wantAppleExportRef.current = false;
          wantGoogleExportRef.current = false;
          return;
        }
      }

      // Build candidates from limitedEvents
      const candidates: CandidateEvent[] = limitedEvents.map((ev) => ({
        title: ev.title,
        event_date: ev.date,
        start_time: ev.time && ev.time.toUpperCase() !== "TBD" ? ev.time : null,
        // #607 — TBD and all-day are opposites; this used to report both as
        // all-day. isAllDayMarker mirrors TodayTab's read-side isAllDayTime.
        is_all_day: isAllDayMarker(ev.time),
        location: ev.location || null,
        notes: ev.notes || null,
        // Carried for consistency with the row insertKinEvents writes, not
        // because dedup reads it: find_event_duplicates takes only
        // (user_id, title, event_date, start_time), so this field changes no
        // matching. Leaving it null while the row says otherwise is the kind
        // of split that later reads as a bug.
        child: chosenChild,
        source_type: sourceType,
      }));

      const dedupResults = await checkEventDuplicatesBatch(user.id, candidates);
      const { autoImport, needsReview, autoSkipped } = partitionDedupResults(dedupResults);

      if (needsReview.length > 0) {
        // Hold the clean ones, ask user to confirm the possibles. The export
        // intent (apple/google refs) is preserved through the review step.
        setPendingCleanResults(autoImport);
        setPendingSkippedResults(autoSkipped);
        setReviewResults(needsReview);
        setReviewChecked(new Set(needsReview.map((_, i) => i)));
        setKinSaveState("review");
        return;
      }

      const batchId = crypto.randomUUID();
      const { inserted, importLogged, insertedIds } = await insertKinEvents(autoImport, batchId);
      await cleanupPendingBatch();
      setSavedCount(inserted);
      setSkippedCount(autoSkipped.length);
      // #1806 — the on-screen count follows the schedule_imports ROW, not the
      // save: insertKinEvents writes it only when it inserted something, and
      // reports whether that write was accepted. A dedup-only save (no row) or
      // a refused insert must not move the counter, or the screen disagrees
      // with the table until the next reload.
      if (importLogged) setMonthlyCount((prev) => prev + 1);
      setKinSaveState("success");
      // Google export takes only the freshly-inserted events (in insertion
      // order); the dedup-skipped ones are handled inside finishSave from
      // autoSkipped, which carries each one's existing row id + Google state.
      const insertedForGoogle = autoImport.map(dedupResultToScheduleEvent);
      const msg = await finishSave(batchId, inserted, autoSkipped, insertedForGoogle, insertedIds);
      // Only NOW — every write above has resolved — does the Apple handoff
      // become reachable. Nothing before this line can navigate.
      let handoff: AppleHandoff | null = null;
      if (wantAppleExportRef.current) {
        handoff = resolveAppleHandoff({
          saveSucceeded: true,
          inserted,
          skipped: autoSkipped.length,
          batchId,
          alsoIds: skippedMatchRowIds(autoSkipped),
          reminder,
        });
        setAppleHandoff(handoff);
      }
      wantAppleExportRef.current = false;
      wantGoogleExportRef.current = false;
      if (msg) toast(msg);
      scheduleCloseUnlessHandoffPending(handoff);
    } catch (err) {
      console.error("[SaveToKin] failed", err);
      const wantedApple = wantAppleExportRef.current;
      // A failed save must not hand her a calendar file for events that
      // aren't in Kin — "blocked" is what stops the link from rendering.
      if (wantedApple) setAppleHandoff({ kind: "blocked" });
      toast.error(
        wantedApple
          ? "I couldn't save those, so I didn't send them to your calendar either 💜 Give it another tap?"
          : "Something went wrong. Please try again."
      );
      setKinSaveState("idle");
      wantAppleExportRef.current = false;
      wantGoogleExportRef.current = false;
    }
  };

  /** Close the import modal shortly after a save — UNLESS the Apple Calendar
   *  handoff just put a link on screen that she still has to tap.
   *
   *  ⚠️ The 800ms auto-close was CORRECT while the .ics fired inside her tap.
   *  The Aug 4, 2026 change (see appleIcsHandoff.ts's header) made the handoff a
   *  SECOND, user-initiated tap on a real https link, and this timer then
   *  unmounted that link 800ms after it rendered — issue #1317. The button
   *  existed for eight-tenths of a second, so the feature read as dead.
   *
   *  ⚠️ Reads the RESOLVED VALUE, never the `appleHandoff` state: setAppleHandoff
   *  has not committed by the time this runs, so the state would still be null
   *  and every save would close as before.
   *
   *  Every non-Apple path (Kin-only, Google) must keep the old behavior, which
   *  is why the default is still to close.
   */
  const scheduleCloseUnlessHandoffPending = (handoff: AppleHandoff | null) => {
    if (handoff?.kind === "ready") return;
    setTimeout(() => onKinSaveComplete?.(), 800);
  };

  /** Combined button: Save to Kin, THEN offer the Apple Calendar handoff.
   *  Save first, always — this handler must never navigate. */
  const handleSaveToKinPlusApple = () => {
    if (!tierResolved) return;
    wantAppleExportRef.current = true;
    // Clear any handoff left over from a previous tap so a stale "ready" link
    // can't be mistaken for this save's result while it's still in flight.
    setAppleHandoff(null);
    void handleSaveToKin();
  };

  /** Combined button: Save to Kin AND push to Google Calendar.
   *  If not connected, kicks off the OAuth flow instead — the user returns to
   *  the import preview after connecting and can tap again. */
  const handleSaveToKinPlusGoogle = () => {
    if (!connected) {
      connect();
      return;
    }
    wantGoogleExportRef.current = true;
    void handleSaveToKin();
  };

  /** onClick target for the review modal's "Save N" button. Like
   *  handleSaveToKinPlusApple, it does no navigation — the Apple handoff is
   *  resolved after the insert below, not before it. */
  const confirmReviewAndSave = async () => {
    if (!user) return;
    const acceptedReview = reviewResults.filter((_, i) => reviewChecked.has(i));
    const finalToInsert = [...pendingCleanResults, ...acceptedReview];
    // Google export: only the actually-inserted events go in here (in insertion
    // order). The dedup-skipped events are handled inside finishSave from
    // pendingSkippedResults — it checks each one's existing Google connection
    // and pushes only the ones still missing a google_event_id.
    const googleExportEvents: ScheduleEvent[] = finalToInsert.map(dedupResultToScheduleEvent);
    if (wantAppleExportRef.current) setAppleHandoff(null);
    setKinSaveState("loading");
    try {
      const batchId = crypto.randomUUID();
      const { inserted, importLogged, insertedIds } = await insertKinEvents(finalToInsert, batchId);
      await cleanupPendingBatch();
      setSavedCount(inserted);
      setSkippedCount(pendingSkippedResults.length);
      // Same rule as handleSaveToKin: the count follows the row (#1806).
      if (importLogged) setMonthlyCount((prev) => prev + 1);
      setKinSaveState("success");
      const msg = await finishSave(batchId, inserted, pendingSkippedResults, googleExportEvents, insertedIds);
      // Same rule as handleSaveToKin: the handoff only exists once every
      // write above has resolved. The dedup-skipped rows still travel with
      // it (via `also`), so the .ics is the full set she selected — that
      // behavior predates this fix and is deliberately preserved.
      let handoff: AppleHandoff | null = null;
      if (wantAppleExportRef.current) {
        handoff = resolveAppleHandoff({
          saveSucceeded: true,
          inserted,
          skipped: pendingSkippedResults.length,
          batchId,
          alsoIds: skippedMatchRowIds(pendingSkippedResults),
          reminder,
        });
        setAppleHandoff(handoff);
      }
      wantAppleExportRef.current = false;
      wantGoogleExportRef.current = false;
      setPendingSkippedResults([]);
      if (msg) toast(msg);
      scheduleCloseUnlessHandoffPending(handoff);
    } catch (err) {
      console.error("[SaveToKin] confirm failed", err);
      const wantedApple = wantAppleExportRef.current;
      if (wantedApple) setAppleHandoff({ kind: "blocked" });
      toast.error(
        wantedApple
          ? "I couldn't save those, so I didn't send them to your calendar either 💜 Give it another tap?"
          : "Something went wrong. Please try again."
      );
      setKinSaveState("idle");
      wantAppleExportRef.current = false;
      wantGoogleExportRef.current = false;
    }
  };

  const cancelReview = () => {
    wantAppleExportRef.current = false;
    wantGoogleExportRef.current = false;
    setAppleHandoff(null);
    setPendingSkippedResults([]);
    setReviewResults([]);
    setReviewChecked(new Set());
    setPendingCleanResults([]);
    setKinSaveState("idle");
  };

  const anySuccess = kinSaveState === "success";

  useEffect(() => {
    if (!anySuccess || suggestionsRequested.current || !onSendMessage) return;
    suggestionsRequested.current = true;
    setSuggestionsLoading(true);

    const eventTitles = limitedEvents.map(e => e.title).join(", ");
    const hiddenPrompt = `[FOLLOW_UP_SUGGESTIONS] I just imported these events to my calendar: ${eventTitles}. Based on what these events look like, give me exactly 3 short follow-up offers. Each one should be something practical you can help me with related to these specific events. Format each one on its own line starting with an emoji and keep each under 10 words. Only give me the 3 lines, nothing else.`;

    (async () => {
      try {
        const { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } = await import("@/integrations/external-supabase/client");
        const CHAT_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-chat`;
        if (!session?.access_token) return;
        const currentDate = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
        const resp = await fetch(CHAT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}`, apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY },
          body: JSON.stringify({ messages: [{ role: "user", content: hiddenPrompt }], currentDate, user_id: user?.id ?? null }),
        });
        if (!resp.ok) {
          // 429/400/401 etc. — silently skip suggestions instead of crashing.
          await resp.json().catch(() => ({}));
          return;
        }
        if (!resp.body) return;

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let fullText = "";
        let buf = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf("\n")) !== -1) {
            let line = buf.slice(0, nl);
            buf = buf.slice(nl + 1);
            if (line.endsWith("\r")) line = line.slice(0, -1);
            if (!line.startsWith("data: ")) continue;
            const json = line.slice(6).trim();
            if (json === "[DONE]") break;
            try {
              const parsed = JSON.parse(json);
              const c = parsed.choices?.[0]?.delta?.content;
              if (c) fullText += c;
            } catch { /* ignore */ }
          }
        }

        const lines = fullText.trim().split("\n").map(l => l.trim()).filter(l => l.length > 0);
        setDynamicSuggestions(lines.slice(0, 3));
      } catch {
        // Silently fail — no suggestions shown
      } finally {
        setSuggestionsLoading(false);
      }
    })();
  }, [anySuccess, limitedEvents, onSendMessage]);

  const renderSuggestions = () => {
    if (!onSendMessage) return null;
    if (suggestionsLoading) {
      return (
        <div style={{ marginTop: "12px", textAlign: "center" }}>
          <span className="inline-block animate-pulse font-body" style={{ fontSize: "16px" }}>✨</span>
        </div>
      );
    }
    if (dynamicSuggestions.length === 0) return null;
    return (
      <div className="animate-fade-in" style={{ marginTop: "12px" }}>
        <p className="font-body" style={{ fontSize: "12px", color: "#54525C" }}>Kin can help you prep for these too:</p>
        <div className="flex flex-col" style={{ gap: "8px", marginTop: "8px" }}>
          {dynamicSuggestions.map((line, idx) => (
            <button
              key={idx}
              onClick={() => onSendMessage(line)}
              className="text-left font-body transition-colors active:underline"
              style={{ fontSize: "14px", color: "#5050C8" }}
            >
              {line}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Nothing renders until the monthly count AND both tier reads have landed —
  // no cap flash for a Pro user on a cold open, and no save button to tap
  // during the window where we can't yet tell whether the cap applies (#363).
  if (!checkedLimit || !tierResolved) return null;

  if (importsExhausted) {
    return (
      <div className="mt-3 rounded-2xl border p-4" style={{ borderColor: "#C8C8F4", backgroundColor: "white", boxShadow: "0 2px 12px rgba(28,26,62,0.06)" }}>
        <p className="font-display text-base" style={{ color: "#1C1A3E" }}>📅 Schedule Import</p>
        <p className="font-body text-sm mt-2" style={{ color: "#54525C" }}>
          You've used your 3 free imports this month. Kin Pro gives you unlimited schedule imports.{" "}
          {/* Issue #50: "Upgrade to Kin Pro" was unlinked text. */}
          <ProUpsellLink label="See what's in Kin Pro 💜" fontSize={14} />
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border p-4" style={{ borderColor: "#C8C8F4", backgroundColor: "white", boxShadow: "0 2px 12px rgba(28,26,62,0.06)" }}>
      {/* Header */}
      <div className="mb-3">
        <p className="font-display text-base" style={{ color: "#1C1A3E" }}>📅 Schedule Import</p>
        <p className="font-body text-xs" style={{ color: "#54525C" }}>
          {selectable ? `${includedEvents.length} of ${activeEvents.length} selected` : `${activeEvents.length} events found`}
        </p>
      </div>

      {/* Bulk edit section */}
      <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #F0F0FF" }}>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={bulkLabel}
            onChange={(e) => setBulkLabel(e.target.value)}
            placeholder="Add a label to all events (e.g. Jake's Soccer)"
            className="flex-1 font-body text-xs border rounded-lg px-2 py-1.5 min-w-0"
            style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
          />
          <button
            onClick={applyBulkLabel}
            disabled={!bulkLabel.trim()}
            className="font-body font-semibold text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-50 flex-shrink-0"
            style={{ backgroundColor: "#5050C8" }}
          >
            Apply to All
          </button>
        </div>
        {appliedLabel && (
          <div className="mt-1.5 flex items-center justify-between">
            <span className="font-body text-xs" style={{ color: "#54525C" }}>
              Label applied: <strong style={{ color: "#1C1A3E" }}>{appliedLabel}</strong>
            </span>
            <button
              onClick={clearBulkLabel}
              className="font-body text-xs underline"
              style={{ color: "#5050C8" }}
            >
              Clear
            </button>
          </div>
        )}
        {/* #1957 item 6 — "Who's this for?". Sets `child` on every row of this
            import, which is the ONLY field the calendar colors by, so the chips
            carry the same colors the legend does. */}
        {kids.length > 0 && (
          <div className="mt-2">
            <div className="font-body text-xs" style={{ color: "#54525C", marginBottom: 5 }}>
              Who's this for?
            </div>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              {chips.map((chip) => {
                const selected = who === chip.key;
                const color =
                  chip.key === WORK_CHIP
                    ? "#54525C"
                    : chip.key === FAMILY_CHIP
                      ? "#5050C8"
                      : (prefs?.known_kid_colors?.[chip.key] ?? "#5050C8");
                return (
                  <button
                    key={chip.key}
                    type="button"
                    data-kin-import-who={chip.key}
                    onClick={() => selectWho(chip.key)}
                    className="font-body inline-flex items-center"
                    style={{
                      gap: 5,
                      minHeight: 32,
                      padding: "5px 10px",
                      borderRadius: 999,
                      border: selected ? `2px solid ${color}` : "1.5px solid #C8C8F4",
                      background: selected ? "#FFFFFF" : "transparent",
                      fontSize: 12,
                      fontWeight: selected ? 700 : 600,
                      color: "#1C1A3E",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        width: 8, height: 8, borderRadius: 999,
                        background: color, display: "inline-block", flexShrink: 0,
                      }}
                    />
                    {chip.label}
                  </button>
                );
              })}
            </div>
            {/* Only while the selection is still Kin's guess — her own tap
                clears it, so this never sits over a chip she chose. */}
            {whoGuess && (
              <div
                className="font-body"
                data-kin-import-who-guess
                style={{ fontSize: 11, color: "#5050C8", marginTop: 5 }}
              >
                {whoGuess}
              </div>
            )}
          </div>
        )}

        {/* #1957 item A — "Remind me:" was a promise Kin does not keep. The
            value is never stored: it reaches Google as a `reminders.overrides`
            popup and an Apple .ics as a VALARM, and goes nowhere at all on a
            plain Save to Kin. There is no reminder column on `events` or
            `imported_events` and nothing in dispatch-notifications reads one,
            so the old label had her expecting a ping from Kin that cannot
            come. The name and the line under it say where the alert actually
            comes from. */}
        <div className="mt-2 flex items-center gap-2">
          <label className="font-body text-xs flex-shrink-0" style={{ color: "#54525C" }}>Calendar reminder:</label>
          <select
            value={reminder}
            onChange={(e) => setReminder(e.target.value as ReminderValue)}
            className="flex-1 font-body text-xs border rounded-lg px-2 py-1.5 bg-white"
            style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
          >
            {REMINDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div
          className="font-body"
          data-kin-reminder-helper
          style={{ fontSize: 11, color: "#54525C", marginTop: 4, lineHeight: 1.4 }}
        >
          Added as an alert in Google or Apple Calendar 💜
        </div>
      </div>

      {/* Schedule-only nudge: vision can drift dates from row-based schedules,
          so prompt the user to spot-check before saving. */}
      {sourceType === "schedule" && activeEvents.length > 0 && (
        <p
          className="font-body"
          style={{ fontSize: 13, color: "#54525C", padding: "6px 0 10px", lineHeight: 1.5 }}
        >
          📅 Quick check: tap any day to fix. Schedules with rows can trip me up sometimes 💜
        </p>
      )}

      {/* Event rows */}
      <div className="flex flex-col" style={{ maxHeight: lockedEvents.length > 0 ? "320px" : "280px", overflowY: "auto" }}>
        {events.map((ev, i) => {
          if (removed.has(i)) return null;
          const activeIdx = activeEvents.indexOf(ev);
          // lockedEvents is empty unless the free cap actually applies (#363),
          // so it alone decides the dimmed/locked row treatment.
          const isLocked = lockedEvents.includes(ev);
          const isEditing = editingIdx === i;

          const isChecked = selected.has(i);
          return (
            <div key={i} style={{ opacity: isLocked ? 0.4 : selectable && !isChecked ? 0.45 : 1 }}>
              {activeIdx > 0 && <div style={{ height: "1px", backgroundColor: "#F0F0FF" }} />}
              <div className="flex items-start gap-3 py-2.5">
                {selectable && (
                  <input
                    type="checkbox"
                    aria-label={`Select ${ev.title}`}
                    checked={isChecked}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(i);
                        else next.delete(i);
                        return next;
                      });
                    }}
                    className="mt-1 flex-shrink-0"
                    style={{ accentColor: "#5050C8", width: 18, height: 18 }}
                  />
                )}
                <div className="flex-shrink-0" style={{ width: "110px" }}>
                  {isEditing ? (
                    <div className="flex flex-col gap-1">
                      <DayChip
                        currentDate={editValues?.date || ""}
                        isOpen={openPickerIdx === i}
                        onToggle={() => setOpenPickerIdx((cur) => (cur === i ? null : i))}
                        onPick={(iso) => {
                          setEditValues((v) => (v ? { ...v, date: iso } : v));
                          setOpenPickerIdx(null);
                        }}
                      />
                      <input value={editValues?.time || ""} onChange={e => setEditValues(v => v ? { ...v, time: e.target.value } : v)} className="font-body text-xs border rounded px-1 py-0.5 w-full" style={{ borderColor: "#C8C8F4" }} />
                    </div>
                  ) : (
                    <>
                      <DayChip
                        currentDate={ev.date}
                        isOpen={openPickerIdx === i}
                        onToggle={() => setOpenPickerIdx((cur) => (cur === i ? null : i))}
                        onPick={(iso) => {
                          setEvents((prev) => prev.map((e, idx) => (idx === i ? { ...e, date: iso } : e)));
                          setOpenPickerIdx(null);
                        }}
                      />
                      <p className="font-body text-xs mt-1" style={{ color: "#54525C" }}>{ev.time}</p>
                    </>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex flex-col gap-1">
                      <input value={editValues?.title || ""} onChange={e => setEditValues(v => v ? { ...v, title: e.target.value } : v)} className="font-body text-sm font-bold border rounded px-1 py-0.5 w-full" style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }} />
                      <input value={editValues?.location || ""} onChange={e => setEditValues(v => v ? { ...v, location: e.target.value } : v)} className="font-body text-xs border rounded px-1 py-0.5 w-full" style={{ borderColor: "#C8C8F4" }} placeholder="Location" />
                    </div>
                  ) : (
                    <>
                      <p className="font-body font-bold text-sm" style={{ color: "#1C1A3E" }}>{ev.title}</p>
                      {ev.location && <p className="font-body text-xs" style={{ color: "#54525C" }}>{ev.location}</p>}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isLocked ? (
                    <Lock size={14} style={{ color: "#54525C" }} />
                  ) : isEditing ? (
                    <>
                      <button onClick={confirmEdit} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-green-50"><Check size={14} style={{ color: "#22c55e" }} /></button>
                      <button onClick={() => { setEditingIdx(null); setEditValues(null); }} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50"><X size={14} style={{ color: "#CC3333" }} /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(i)} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"><Pencil size={14} style={{ color: "#54525C" }} /></button>
                      <button onClick={() => setRemoved(prev => new Set(prev).add(i))} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50"><X size={14} style={{ color: "#CC3333" }} /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {lockedEvents.length > 0 && (
        <p className="font-body text-xs mt-2" style={{ color: "#5050C8" }}>
          This schedule has {lockedEvents.length} more events. Kin Pro imports them all.{" "}
          {/* Issue #50: "Upgrade to Kin Pro" was unlinked text. */}
          <ProUpsellLink label="See what's in Kin Pro 💜" fontSize={12} />
        </p>
      )}

      {/* Action buttons */}
      <div className="mt-3">
        {kinSaveState === "success" ? (
          <div className="w-full py-3 rounded-xl text-center font-body font-semibold text-sm" style={{ color: "#5050C8" }}>
            {savedCount === 0 && skippedCount > 0
              ? "💜 Already in your week"
              : skippedCount > 0
                ? `💜 Saved to Kin — ${savedCount} ${savedCount === 1 ? "event" : "events"} added (${skippedCount} already saved)`
                : `💜 Saved to Kin — ${savedCount} ${savedCount === 1 ? "event" : "events"} added to your week`}
          </div>
        ) : (
          <>
            {enableKinSave && (
              <button
                onClick={handleSaveToKin}
                disabled={kinSaveState === "loading" || limitedEvents.length === 0}
                className="w-full font-body font-semibold text-white rounded-lg py-3 mb-2 disabled:opacity-50 transition-all hover:opacity-90"
                style={{ backgroundColor: "#5050C8", fontSize: 14 }}
              >
                {kinSaveState === "loading" ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Tucking it away...
                  </span>
                ) : (
                  "💜 Save to Kin"
                )}
              </button>
            )}
            <div className="flex gap-2">
              {/* Save to Kin + Google Calendar — when not connected, kicks off
                  OAuth and the user returns here to tap again. */}
              <button
                onClick={handleSaveToKinPlusGoogle}
                disabled={kinSaveState === "loading" || limitedEvents.length === 0}
                className="flex-1 py-3 rounded-xl font-body font-semibold text-sm text-white disabled:opacity-50 transition-all hover:opacity-90"
                style={{ backgroundColor: enableKinSave ? "#9090E8" : "#5050C8" }}
              >
                {kinSaveState === "loading" && wantGoogleExportRef.current ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Tucking it away...
                  </span>
                ) : connected ? (
                  "💜📅 Save to Kin + Google Calendar"
                ) : (
                  "Connect Google"
                )}
              </button>

              {/* Save to Kin + Apple Calendar (.ics) — runs the same dedup/save
                  pipeline as the standalone Kin button, then offers the handoff
                  link. Hidden when her Apple Calendar already subscribes to
                  Kin (#1647): plain Save to Kin covers it. */}
              {!kinFeedActive && (
              <button
                onClick={handleSaveToKinPlusApple}
                disabled={kinSaveState === "loading" || limitedEvents.length === 0}
                className="flex-1 py-3 rounded-xl font-body font-semibold text-sm text-white disabled:opacity-50 transition-all hover:opacity-90"
                style={{ backgroundColor: enableKinSave ? "#54525C" : "#1C1A3E" }}
              >
                {kinSaveState === "loading" && wantAppleExportRef.current ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Tucking it away...
                  </span>
                ) : (
                  "💜🍎 Save to Kin + Apple Calendar"
                )}
              </button>
              )}
            </div>

            {/* #1957 item 3 — what "Save to Kin" already does for her Apple
                Calendar, said out loud.
                ⚠️ Gated on `kinFeedActive`, which is the Kin → Apple
                SUBSCRIPTION, and that is the only state in which this sentence
                is true. The other Apple connection (useAppleCalendar) reads her
                iCloud share link INTO Kin and carries nothing back out, so
                showing this to someone with only that linked would promise a
                sync that does not exist.
                The refresh caveat is not hedging: the feed advertises
                REFRESH-INTERVAL PT1H, but that is a hint and iOS re-polls a
                subscribed calendar on its own schedule — so "automatically"
                without it would have her looking for an event that is genuinely
                not there yet. */}
            {kinFeedActive && (
              <p
                className="font-body"
                data-kin-apple-auto-note
                style={{ fontSize: 11.5, color: "#54525C", marginTop: 8, lineHeight: 1.4 }}
              >
                Apple Calendar linked? Anything you save to Kin lands there
                automatically (Apple can take a few hours to refresh) 💜
              </p>
            )}
          </>
        )}

        {/* Apple Calendar handoff card — renders ONLY after the save has
            already landed. This is the second tap: the user's own gesture on
            a real link to a real https URL, which is what makes it immune to
            both the popup-blocking f7dc0c4 was dodging and the download
            interceptor that ate the data: URI. */}
        {appleHandoff?.kind === "ready" && (
          <>
            <div
              className="mt-3 animate-fade-in"
              style={{
                backgroundColor: "#F8F8FF",
                border: "1px solid #C8C8F4",
                borderRadius: "12px",
                padding: "12px",
              }}
            >
              <p className="font-body" style={{ fontSize: "13px", color: "#1C1A3E", lineHeight: 1.5 }}>
                Saved to Kin 💜 One more tap to put them on your Apple Calendar too — your phone will ask if you want to add them. Tap{" "}
                <strong className="font-semibold" style={{ color: "#5050C8" }}>Add All</strong> and you're done.
              </p>
              {/* The second tap. A plain <a href> to the real https .ics URL — her
                  own gesture, so there is no user-activation deadline to beat
                  and nothing for a popup blocker to swallow.

                  #1639 — inside the Median shell the tap is routed out to
                  Safari. Sep 17 2026, on device: the in-webview navigation did
                  NOTHING. No Calendar sheet, no file sheet, no error — a
                  WKWebView cannot render text/calendar, and the rebuilt shell
                  swallows the response silently. (Before the Jul 23-25 shell
                  rebuild the same class of navigation produced Median's file
                  sheet; the data: URI history is in schedule-ics/index.ts.)
                  Safari renders the same URL as Calendar's "Add All Events"
                  sheet, and Safari is a separate app she can always leave, so
                  it cannot dead-end. Outside Median (Safari, the PWA) the link
                  stays an ordinary link — long-press and copy work normally.

                  The better long-term fix is a Median Link Handling rule for
                  app.kinyoudoitall.com/ics, which needs a wrapper rebuild +
                  App Store review. Until then this is the launch-safe path. */}
              <a
                href={appleHandoff.url}
                onClick={(e) => {
                  if (!isMedianShellUA(navigator.userAgent)) return;
                  e.preventDefault();
                  openExternal(appleHandoff.url);
                }}
                className="mt-2 w-full inline-block text-center py-3 rounded-xl font-body font-semibold text-sm text-white no-underline"
                style={{ backgroundColor: "#5050C8" }}
              >
                🍎 Add to Apple Calendar
              </a>
              {/* #1647 — the way out of doing this every time. One line, no
                  link: Settings is one tap away and the row is labeled. */}
              <p className="font-body mt-2" style={{ fontSize: 12, color: "#54525C", lineHeight: 1.5 }}>
                Want this to be automatic? Add Kin to Apple Calendar once in Settings and every import lands there on its own.
              </p>
              {/* #1317 — with the auto-close suppressed, the card needs its own
                  dismissal or she is stranded in the import modal. Secondary
                  weight on purpose: it must never compete with the link above,
                  which is the thing she came here to tap. */}
              <button
                type="button"
                data-testid="apple-handoff-done"
                onClick={() => onKinSaveComplete?.()}
                className="mt-2 w-full py-2 rounded-xl font-body font-semibold text-sm"
                style={{ backgroundColor: "transparent", color: "#54525C" }}
              >
                All set
              </button>
            </div>
            {renderSuggestions()}
          </>
        )}
      </div>

      {/* Review modal: shown when possible duplicates were detected during Save to Kin */}
      {kinSaveState === "review" && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: "rgba(28,26,62,0.55)" }}
          onClick={cancelReview}
        >
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl sm:rounded-2xl p-5 max-h-[80vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={cancelReview}
              aria-label="Close"
              className="absolute top-3 right-3 flex items-center justify-center rounded-full active:opacity-70"
              style={{ width: 32, height: 32, color: "#54525C" }}
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-display pr-8" style={{ fontSize: 18, color: "#1C1A3E" }}>
              Already in your Kin?
            </h3>
            <p className="font-body mt-1" style={{ fontSize: 13, color: "#54525C" }}>
              We found {reviewResults.length} {reviewResults.length === 1 ? "event that looks similar" : "events that look similar"} to ones already saved. Uncheck any you want to skip.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              {reviewResults.map((r, idx) => {
                const checked = reviewChecked.has(idx);
                const existing = r.matches[0];
                return (
                  <label
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                    style={{ backgroundColor: "#F8F8FF", border: "1px solid #E0DEEE" }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setReviewChecked((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(idx);
                          else next.delete(idx);
                          return next;
                        });
                      }}
                      className="mt-1"
                      style={{ accentColor: "#5050C8" }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-body font-semibold text-sm" style={{ color: "#1C1A3E" }}>
                        {r.candidate.title}
                      </p>
                      <p className="font-body text-xs" style={{ color: "#54525C" }}>
                        {r.candidate.event_date}
                        {r.candidate.start_time ? ` · ${r.candidate.start_time}` : ""}
                      </p>
                      {existing && (
                        <p className="font-body text-xs mt-1 italic" style={{ color: "#9090E8" }}>
                          Similar to: {existing.title}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={cancelReview}
                className="flex-1 py-3 rounded-xl font-body font-semibold text-sm"
                style={{ backgroundColor: "#F0F0FF", color: "#1C1A3E" }}
              >
                Skip All
              </button>
              <button
                onClick={confirmReviewAndSave}
                disabled={pendingCleanResults.length + reviewChecked.size === 0}
                className="flex-1 py-3 rounded-xl font-body font-semibold text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#5050C8" }}
              >
                Save {pendingCleanResults.length + reviewChecked.size}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulePreview;
